/**
 * One error shape for everything the help chat can fail at.
 *
 * The chat-service answers `POST /api/chat/messages` either with an SSE stream
 * or — when the turn is rejected before streaming starts — with a JSON body
 * `{error, code, reason?, retryAfter?, resetsAt?}` (see the service's
 * `chatMessagesRoute.ts`). A turn that fails *after* streaming started instead
 * emits an SSE `error` event carrying `{code, message}`. Auth failures and
 * crashes come back as bare `{error}` bodies with no `code` at all.
 *
 * All of those funnel into `ChatRequestError` so the widget has exactly one
 * thing to catch and exactly one function to ask for a user-facing message.
 */
export class ChatRequestError extends Error {
  /** HTTP status, or 0 when the request never reached the server. */
  readonly status: number;
  /** Machine-readable code from the service, when it sent one. */
  readonly code: string | null;
  /** Internal detail (`moderator:PROMPT_INJECTION`, an exception message, …).
   *  Logged, never shown — it can carry provider/internal wording. */
  readonly reason: string | null;
  readonly retryAfter: number | null;
  readonly resetsAt: string | null;

  constructor(
    status: number,
    code: string | null = null,
    reason: string | null = null,
    retryAfter: number | null = null,
    resetsAt: string | null = null
  ) {
    super(`chat request failed (status=${status} code=${code ?? '-'} reason=${reason ?? '-'})`);
    this.name = 'ChatRequestError';
    this.status = status;
    this.code = code;
    this.reason = reason;
    this.retryAfter = retryAfter;
    this.resetsAt = resetsAt;
  }
}

/** Service `code` → translation key. Codes are the contract; statuses are the
 *  fallback for the paths that carry no code (auth middleware, 500 handler). */
const KEY_BY_CODE: Readonly<Record<string, string>> = {
  // Pre-stream rejections (HTTP 400/409/429 + JSON body)
  input_rejected: 'Help_chat_error_rejected_tr',
  input_too_long: 'Help_chat_error_too_long_tr',
  input_empty: 'Help_chat_error_empty_tr',
  invalid_body: 'Help_chat_error_generic_tr',
  stream_in_progress: 'Help_chat_error_in_progress_tr',
  rate_limited: 'Help_chat_error_rate_limited_tr',
  budget_exhausted: 'Help_chat_error_budget_tr',
  // Mid-stream failures (SSE `error` event)
  provider_unavailable: 'Help_chat_error_provider_tr',
  stream_timeout: 'Help_chat_error_timeout_tr',
  stream_idle_timeout: 'Help_chat_error_timeout_tr',
  prompt_leak_detected: 'Help_chat_error_generic_tr',
  persistence_failed: 'Help_chat_error_generic_tr',
  // Client-side
  network: 'Help_chat_error_network_tr',
  request_timeout: 'Help_chat_error_timeout_tr',
  parse_error: 'Help_chat_error_generic_tr',
};

const GENERIC_KEY = 'Help_chat_error_generic_tr';

/**
 * Translation key for anything thrown on a chat code path. Unknown errors map
 * to the generic key rather than surfacing raw text — a provider exception
 * message is not something a customer should read.
 */
export function chatErrorKey(err: unknown): string {
  if (!(err instanceof ChatRequestError)) return GENERIC_KEY;
  if (err.code && KEY_BY_CODE[err.code]) return KEY_BY_CODE[err.code];
  if (err.status === 0) return 'Help_chat_error_network_tr';
  if (err.status === 401 || err.status === 403) return 'Help_chat_error_auth_tr';
  if (err.status === 429) return 'Help_chat_error_rate_limited_tr';
  return GENERIC_KEY;
}

/** Build a `ChatRequestError` from an SSE `error` event payload. */
export function chatErrorFromSseEvent(payload: unknown, status = 200): ChatRequestError {
  const p = (payload ?? {}) as Record<string, unknown>;
  return new ChatRequestError(
    status,
    typeof p['code'] === 'string' ? p['code'] : null,
    typeof p['message'] === 'string' ? p['message'] : null
  );
}

/** Build a `ChatRequestError` from a non-OK `fetch` response, reading the
 *  service's JSON error body when there is one. */
export async function chatErrorFromResponse(response: Response): Promise<ChatRequestError> {
  let body: Record<string, unknown> = {};
  try {
    const parsed: unknown = await response.json();
    if (parsed && typeof parsed === 'object') body = parsed as Record<string, unknown>;
  } catch {
    // 204/empty/HTML error page — status alone drives the message.
  }
  return new ChatRequestError(
    response.status,
    typeof body['code'] === 'string' ? body['code'] : null,
    typeof body['reason'] === 'string' ? body['reason'] : typeof body['error'] === 'string' ? body['error'] : null,
    typeof body['retryAfter'] === 'number' ? body['retryAfter'] : null,
    typeof body['resetsAt'] === 'string' ? body['resetsAt'] : null
  );
}
